import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetGenericAccountByIdMutation } from "../../../api/services/client/clientApis";
import {
  ITabs,
  MuiFooterButtons,
  MuiTabPanel,
  MuiTabs,
  STATUS_BADGE_TYPE,
  WarningConfirmationModal,
} from "../../../components";
import { PAGE_IDS } from "../../../helpers/constants";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { useToast } from "../../../hooks/useToast";
import { toggleLoader } from "../../../redux/slices/appState/appStateSlice";
import { CreateGenericAccountHeader } from "../createGenericAccount/components/createGenericAccountHeader/CreateGenericAccountHeader";
import { createGenericAccounts } from "../createGenericAccount/siteCore/SiteCoreKeys";
import RegisteredParticipantsTab from "../viewGenericAccount/registeredParticipantsTab/RegisteredParticipantsTab";
import {
  convertToUserFormat,
  convertToUTCFormat,
  getValueOrBlank,
  getValueOrBlankArray,
  isNotEmpty,
} from "../../../helpers/utils";
import { CreateGenericAccountFormKey } from "../createGenericAccount/modelsAndSchemas/Models";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CreateGenericAccountValidationSchema } from "../createGenericAccount/modelsAndSchemas/ValidationSchema";
import dayjs from "dayjs";
import { useGetLoggedInUserDetails } from "../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { getDateFormatByCountry } from "../../../helpers/constants/countryDateFormat";
import { IdNameList } from "../../../api/services/common/models";
import { GetGenericAccountsResponse } from "../../../api/services/client/models";
import { EditGenericAccountTabWrapper } from "./EditGenericAccountTab.style";
import {
  EditGenericAccount,
  EditGenericAccountRefProps,
} from "../editGenericAccount/EditGenericAccount";

export const GENERIC_ACCOUNT_TAB_INDEX = {
  GenericAccountDetails: 0,
  RegisteredParticipants: 1,
} as const;

const pageId = PAGE_IDS.EDIT_GENERIC_ACCOUNT;

export const EditGenericAccountTab: FC = () => {
  const { useFetchLabel, commonLabels, commonValidationMessages, fetchLabel } =
    useSiteCore();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const editGenericRef = useRef<EditGenericAccountRefProps>(null);
  const [showUpdateConfirmationModal, setShowUpdateConfirmationModal] =
    useState<boolean>(false);
  const [showCloseConfirmationModal, setShowCloseConfirmationModal] =
    useState<boolean>(false);

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const location = useLocation();
  const genericAccountId = location?.state?.genericAccountId;
  const tabIndex = location?.state?.tabIndex;
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(
    tabIndex ?? GENERIC_ACCOUNT_TAB_INDEX.GenericAccountDetails,
  );

  const {
    mutateAsync: getAccountDetailsById,
    data: genericAccountData,
    isPending: isGetAccountDetailsByIdPending,
    isSuccess: isGetAccountDetailsByIdSuccess,
  } = useGetGenericAccountByIdMutation();

  const {
    createGenericAccountGenericAccountHeaderTitle,
    genericAccountDetailsTab,
    registeredParticipantsTab,
    pleaseUpdateTheGenericAccountDetailsToProceed,
    areYouSureYouWantToCancelTheGenericAccount,
    genericAccountDetailsUpdatedMessage,
    createGenericAccountBasicDetailsTitle,
    expirationDateMustBeGreaterThanStartDate,
    startDateMustBeLesserThanExpirationDate,
  } = createGenericAccounts;

  const getBasicDetailsLabel = (key: string) => {
    return fetchLabel(createGenericAccountBasicDetailsTitle, key);
  };
  const getUserFormattedDate = (date: string) => {
    return convertToUserFormat(date, userCountryDateFormat);
  };

  const getDefaultFormValues = (data?: GetGenericAccountsResponse) => {
    const updatedData = genericAccountData ?? data;
    const startDateFormatted = getUserFormattedDate(
      getValueOrBlank(updatedData?.startDate),
    );
    const deliveryOfficesIds = getValueOrBlankArray(
      updatedData?.deliveryOffices,
    ).map((item: IdNameList) => item.id);

    return {
      [CreateGenericAccountFormKey.genericAccountName]: getValueOrBlank(
        updatedData?.name,
      ),
      [CreateGenericAccountFormKey.purpose]: getValueOrBlank(
        updatedData?.purposeId,
      ),
      [CreateGenericAccountFormKey.status]:
        updatedData?.status ?? STATUS_BADGE_TYPE.active,
      [CreateGenericAccountFormKey.clientId]: getValueOrBlank(
        updatedData?.client?.id,
      ),
      [CreateGenericAccountFormKey.clientName]: getValueOrBlank(
        updatedData?.client?.name,
      ),
      [CreateGenericAccountFormKey.genericRegistrationCode]: getValueOrBlank(
        updatedData?.registrationCode,
      ),
      [CreateGenericAccountFormKey.startDate]: startDateFormatted,
      [CreateGenericAccountFormKey.expirationDate]: getUserFormattedDate(
        getValueOrBlank(updatedData?.expirationDate),
      ),
      [CreateGenericAccountFormKey.numberOfAccounts]: getValueOrBlank(
        updatedData?.numberOfAccounts?.assigned?.toString(),
      ),
      [CreateGenericAccountFormKey.programDuration]: getValueOrBlank(
        updatedData?.programDuration.toString(),
      ),
      [CreateGenericAccountFormKey.requestedBy]: getValueOrBlank(
        updatedData?.requestedByUserId?.email,
      ),
      [CreateGenericAccountFormKey.nextView]: getValueOrBlank(
        updatedData?.solutionOffering?.id,
      ),
      [CreateGenericAccountFormKey.country]: getValueOrBlank(
        updatedData?.country?.id,
      ),
      [CreateGenericAccountFormKey.features]: getValueOrBlankArray(
        updatedData?.features,
      ),
      [CreateGenericAccountFormKey.nextProgramClass]: getValueOrBlank(
        updatedData?.programClass?.id,
      ),
      [CreateGenericAccountFormKey.deliveryOffice]: deliveryOfficesIds,
      [CreateGenericAccountFormKey.startDateWithoutFormatted]: dayjs(
        convertToUTCFormat(startDateFormatted, userCountryDateFormat),
      ),
      [CreateGenericAccountFormKey.expirationDateWithoutFormatted]: dayjs(
        getValueOrBlank(updatedData?.expirationDate),
      )
        .utc(false)
        .startOf("day"),
    };
  };

  const methods = useForm({
    mode: "onBlur",
    defaultValues: getDefaultFormValues(),
    resolver: yupResolver(
      CreateGenericAccountValidationSchema({
        ...commonValidationMessages,
        expirationDateMustBeGreaterThanStartDateErrorMessage:
          getBasicDetailsLabel(expirationDateMustBeGreaterThanStartDate),
        startDateMustBeLesserThanExpirationDateErrorMessage:
          getBasicDetailsLabel(startDateMustBeLesserThanExpirationDate),
      }),
    ),
  });

  const {
    reset,
    formState: { touchedFields },
  } = methods;

  useEffect(() => {
    if (
      isGetAccountDetailsByIdSuccess &&
      genericAccountData &&
      isNotEmpty(genericAccountData)
    ) {
      const updatedDefaultValues = getDefaultFormValues(genericAccountData);
      reset(updatedDefaultValues);
    }
  }, [isGetAccountDetailsByIdSuccess, genericAccountData]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        editGenericAccountPending: {
          isVisible: isGetAccountDetailsByIdPending,
        },
      }),
    );

    return () => {
      dispatch(
        toggleLoader({
          editGenericAccountPending: {
            isVisible: false,
          },
        }),
      );
    };
  }, [isGetAccountDetailsByIdPending]);

  const callGetAccountDetailsById = () => {
    if (genericAccountId) {
      getAccountDetailsById(genericAccountId);
    }
  };

  useEffect(() => {
    callGetAccountDetailsById();
  }, [genericAccountId]);

  const getLabel = useFetchLabel(createGenericAccountGenericAccountHeaderTitle);

  const tabIdGenericAccountDetailsTab = `${getLabel(genericAccountDetailsTab)}-tab`;
  const tabIdRegisteredParticipantsTab = `${getLabel(registeredParticipantsTab)}-tab`;

  const EDIT_GENERIC_ACCOUNT_TABS: ITabs[] = [
    {
      id: tabIdGenericAccountDetailsTab,
      label: getLabel(genericAccountDetailsTab),
    },
    {
      id: tabIdRegisteredParticipantsTab,
      label: getLabel(registeredParticipantsTab),
    },
  ];

  const isGenericAccountDetailsTabSelected = useMemo(
    () => selectedTabIndex === GENERIC_ACCOUNT_TAB_INDEX.GenericAccountDetails,
    [selectedTabIndex],
  );

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    if (isNotEmpty(touchedFields)) setShowUpdateConfirmationModal(true);
    else setSelectedTabIndex(value);
  };

  const handleCloseClick = () => {
    if (isGenericAccountDetailsTabSelected && isNotEmpty(touchedFields)) {
      setShowCloseConfirmationModal(true);
    } else {
      navigate(-1);
    }
  };
  const handleUpdateClick = () => {
    if (isGenericAccountDetailsTabSelected) {
      editGenericRef?.current?.triggerApi();
    } else {
      displayToast(getLabel(genericAccountDetailsUpdatedMessage), "success");
    }
  };

  const handleConfirmationModalClose = () => {
    setShowUpdateConfirmationModal(false);
  };

  const handleCloseConfirmationSecondaryClick = () => {
    setShowCloseConfirmationModal(false);
  };

  const handleCloseConfirmationPrimaryClick = () => {
    setShowCloseConfirmationModal(false);
    navigate(-1);
  };

  return (
    <EditGenericAccountTabWrapper>
      <CreateGenericAccountHeader isEdit={true} />

      <MuiTabs
        tabs={EDIT_GENERIC_ACCOUNT_TABS}
        value={selectedTabIndex}
        onChange={onTabChange}
        tabsTestIds={EDIT_GENERIC_ACCOUNT_TABS.map(
          (_, index) => `${pageId}-tab${index + 1}`,
        )}
        className="program-participant-tab"
      >
        <MuiTabPanel
          panelId={`${pageId}-tab${selectedTabIndex + 1}`}
          value={selectedTabIndex}
          index={GENERIC_ACCOUNT_TAB_INDEX.GenericAccountDetails}
          isPanelWithMount
          ariaLabelledById={EDIT_GENERIC_ACCOUNT_TABS[selectedTabIndex]?.id}
        >
          <FormProvider {...methods}>
            <EditGenericAccount
              ref={editGenericRef}
              genericAccountData={genericAccountData}
            />
          </FormProvider>
        </MuiTabPanel>
        <MuiTabPanel
          panelId={`${pageId}-tab${selectedTabIndex + 1}`}
          value={selectedTabIndex}
          index={GENERIC_ACCOUNT_TAB_INDEX.RegisteredParticipants}
          isPanelWithMount
          ariaLabelledById={EDIT_GENERIC_ACCOUNT_TABS[selectedTabIndex]?.id}
        >
          <RegisteredParticipantsTab
            data={genericAccountData}
            isFromEditGenericAccount={true}
          />
        </MuiTabPanel>
      </MuiTabs>

      <MuiFooterButtons
        secondaryButtonLabel={commonLabels.close}
        primaryButtonLabel={commonLabels.update}
        onSecondaryClick={handleCloseClick}
        onPrimaryClick={handleUpdateClick}
      />

      <WarningConfirmationModal
        keepMounted
        labelContent={getLabel(pleaseUpdateTheGenericAccountDetailsToProceed)}
        show={showUpdateConfirmationModal}
        onPrimaryButtonClick={handleConfirmationModalClose}
        primaryButtonTitle={commonLabels.ok}
        showSecondaryButton={false}
        onClose={handleConfirmationModalClose}
      />

      <WarningConfirmationModal
        keepMounted
        labelContent={getLabel(areYouSureYouWantToCancelTheGenericAccount)}
        show={showCloseConfirmationModal}
        modalTitle={commonLabels.confirmation}
        onPrimaryButtonClick={handleCloseConfirmationPrimaryClick}
        primaryButtonTitle={commonLabels.yes}
        secondaryButtonTitle={commonLabels.no}
        onSecondaryButtonClick={handleCloseConfirmationSecondaryClick}
        onClose={handleCloseConfirmationSecondaryClick}
      />
    </EditGenericAccountTabWrapper>
  );
};
